import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_ROOT } from "../service/Services";

export default function CampaignStatus() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tokenFromState = location.state?.campaignToken;
  const campaignToken = token || tokenFromState;

  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [detailFilter, setDetailFilter] = useState("all"); // all | sent | failed
  const [showAllDetails, setShowAllDetails] = useState(true);
  const [deletingRows, setDeletingRows] = useState(new Set()); // NEW: track rows being excluded

  const detailsRef = useRef(null);

  const formatFullDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const authHeaders = useMemo(
    () => ({
      headers: {
        authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
    []
  );

  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const res = await axios.get(`${API_ROOT}/sales/campaigns`, authHeaders);
      if (res.data?.success && Array.isArray(res.data.data)) {
        setCampaigns(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const fetchStatus = async () => {
    if (!campaignToken) return;
    try {
      const requestLimit = showAllDetails ? 100 : limit; // backend clamps to max 100
      const requestPage = showAllDetails ? 1 : page;
      const res = await axios.get(
        `${API_ROOT}/sales/campaign-status/${campaignToken}?page=${requestPage}&limit=${requestLimit}`,
        authHeaders
      );
      if (res.data?.success) {
        const baseStatus = {
          status: res.data.status,
          sent: res.data.sent ?? 0,
          failed: res.data.failed ?? 0,
          total: res.data.total ?? 0,
          cancelRequested: !!res.data.cancelRequested,
          paused: !!res.data.paused,
          details: Array.isArray(res.data.details) ? res.data.details : [],
          pagination: res.data.pagination || { page: 1, limit: requestLimit, total: 0, pages: 1 },
          summaryBySender: Array.isArray(res.data.summaryBySender) ? res.data.summaryBySender : [],
          topic: res.data.topic ?? "",
          subject: res.data.subject ?? "",
          startedAt: res.data.startedAt ?? null,
          completedAt: res.data.completedAt ?? null,
        };

        const isActivelySending = baseStatus.status === "sending" && !baseStatus.paused;
        const totalPages = baseStatus.pagination?.pages || 1;

        if (showAllDetails && !isActivelySending && totalPages > 1) {
          const allDetails = [...baseStatus.details];
          for (let p = 2; p <= totalPages; p++) {
            try {
              const nextRes = await axios.get(
                `${API_ROOT}/sales/campaign-status/${campaignToken}?page=${p}&limit=${requestLimit}`,
                authHeaders
              );
              if (nextRes.data?.success && Array.isArray(nextRes.data.details)) {
                allDetails.push(...nextRes.data.details);
              }
            } catch (e) {
              break;
            }
          }

          setStatus({
            ...baseStatus,
            details: allDetails,
            pagination: {
              ...baseStatus.pagination,
              page: 1,
              limit: requestLimit,
              total: baseStatus.pagination?.total ?? allDetails.length,
              pages: 1,
            },
          });
        } else {
          setStatus(baseStatus);
        }
        setError(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Campaign not found or already finished.");
      } else {
        setError(err.response?.data?.message || err.message || "Failed to load status");
      }
    }
  };

  // NEW: Exclude / Delete from master data (mark as excluded for future campaigns)
  const handleExcludeRecipient = async (rowNumber, email) => {
    if (!campaignToken || rowNumber == null) return;

    const key = rowNumber.toString();
    if (deletingRows.has(key)) return;

    setDeletingRows((prev) => new Set([...prev, key]));

    try {
      await axios.post(
        `${API_ROOT}/sales/exclude-recipient`,
        {
          campaignToken,
          rowNumber,
          email: email || undefined,
        },
        authHeaders
      );

      // Refresh status to reflect changes
      await fetchStatus();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to exclude recipient";
      setError(msg);
      console.error("Exclude failed:", err);
    } finally {
      setDeletingRows((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  useEffect(() => {
    if (!campaignToken) {
      fetchCampaigns();
    }
  }, [campaignToken]);

  useEffect(() => {
    if (!campaignToken) return;
    setPage(1);
    setDetailFilter("all");
    setShowAllDetails(true);
  }, [campaignToken]);

  useEffect(() => {
    if (!campaignToken) return;
    fetchStatus();
  }, [campaignToken, page, showAllDetails]);

  useEffect(() => {
    if (!campaignToken || !status) return;
    if (status.status === "sending" && !status.paused && !status.cancelRequested) {
      const interval = setInterval(fetchStatus, 2000);
      return () => clearInterval(interval);
    }
  }, [campaignToken, status?.status, status?.paused, status?.cancelRequested]);

  const handleStop = async () => {
    if (!campaignToken) return;
    setActionLoading("stop");
    try {
      await axios.post(`${API_ROOT}/sales/cancel-campaign`, { campaignToken }, authHeaders);
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.message || "Stop failed");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = async () => {
    if (!campaignToken) return;
    setActionLoading("pause");
    try {
      await axios.post(`${API_ROOT}/sales/pause-campaign`, { campaignToken }, authHeaders);
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.message || "Pause failed");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async () => {
    if (!campaignToken) return;
    setActionLoading("resume");
    try {
      await axios.post(`${API_ROOT}/sales/resume-campaign`, { campaignToken }, authHeaders);
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.message || "Resume failed");
    } finally {
      setActionLoading(null);
    }
  };

  const isLive = status && (status.status === "sending" || status.paused);
  const total = status?.total ?? 0;
  const done = (status?.sent ?? 0) + (status?.failed ?? 0);
  const progressPct = total > 0 ? Math.round((done / total) * 100) : 0;
  const details = status?.details ?? [];
  const filteredDetails = useMemo(() => {
    if (detailFilter === "failed") return details.filter((d) => d?.status === "failed");
    if (detailFilter === "sent") return details.filter((d) => d?.status !== "failed");
    return details;
  }, [details, detailFilter]);

  const pagination = status?.pagination || { page: 1, limit: 50, total: 0, pages: 1 };

  const rowNumberRange = useMemo(() => {
    if (!filteredDetails.length) return null;
    const nums = filteredDetails
      .map((d, idx) =>
        d.rowNumber != null ? d.rowNumber : (pagination.page - 1) * pagination.limit + idx + 1
      )
      .filter((n) => typeof n === "number" && !Number.isNaN(n));
    if (!nums.length) return null;
    return { start: Math.min(...nums), end: Math.max(...nums) };
  }, [filteredDetails, pagination.page, pagination.limit]);

  const scrollToDetails = () => {
    if (detailsRef.current) {
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleTileClick = (filter) => {
    setDetailFilter(filter);
    scrollToDetails();
  };

  // ────────────────────────────────────────────────────────────────
  //  No token → Show list of campaigns
  // ────────────────────────────────────────────────────────────────
  if (!campaignToken) {
    return (
      <div className="min-h-full">
        <div className="max-w-[1200px] mx-auto">
          <div className="mb-6 lg:mb-8">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Campaigns</h1>
            <p className="mt-1 text-sm text-slate-500">
              View live and past campaign status. Click a row to see details.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <button
              type="button"
              onClick={() => navigate("/sales")}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700"
            >
              Go to Sales Team Data
            </button>
            <button
              type="button"
              onClick={fetchCampaigns}
              disabled={campaignsLoading}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
            >
              {campaignsLoading ? "Loading…" : "Refresh list"}
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
            {campaignsLoading && campaigns.length === 0 ? (
              <div className="p-12 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-500" />
                <p className="mt-3 text-sm text-slate-500">Loading campaigns…</p>
              </div>
            ) : campaigns.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-slate-500 text-sm">No campaigns yet.</p>
                <p className="mt-1 text-slate-400 text-xs">
                  Start a mail campaign from Sales Team Data to see live status here.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/sales")}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-emerald-600 hover:bg-emerald-50"
                >
                  Go to Sales Team Data
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Topic / Subject</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Selected rows</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Sent</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Failed</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Started</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-20">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {campaigns.map((c) => (
                      <tr
                        key={c.token}
                        className="hover:bg-slate-50/80 transition-colors cursor-pointer"
                        onClick={() => navigate(`/campaign-status/${c.token}`)}
                      >
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${
                              c.isLive
                                ? "bg-emerald-100 text-emerald-800"
                                : c.status === "completed"
                                  ? "bg-blue-100 text-blue-800"
                                  : c.status === "cancelled"
                                    ? "bg-slate-100 text-slate-700"
                                    : c.status === "failed"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {c.isLive && (
                              <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                              </span>
                            )}
                            {c.isLive ? "Live" : c.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 max-w-[220px]">
                          <span className="font-medium text-slate-800 truncate block" title={c.topic || c.subject}>
                            {c.topic || c.subject || "—"}
                          </span>
                          {c.subject && c.topic !== c.subject && (
                            <span className="text-xs text-slate-500 truncate block" title={c.subject}>{c.subject}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-700">
                          {c.startRowNumber != null && c.endRowNumber != null
                            ? c.startRowNumber === c.endRowNumber
                              ? c.startRowNumber.toLocaleString()
                              : `${c.startRowNumber.toLocaleString()}-${c.endRowNumber.toLocaleString()}`
                            : c.total == null || c.total <= 0
                              ? "—"
                              : c.total === 1
                                ? "1"
                                : `1-${c.total.toLocaleString()}`}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-emerald-600">{c.sent}</td>
                        <td className="px-4 py-3 text-right font-medium text-red-600">{c.failed}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{c.total}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                          {c.startedAt ? formatFullDateTime(c.startedAt) : "—"}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => navigate(`/campaign-status/${c.token}`)}
                            className="text-emerald-600 hover:text-emerald-700 font-medium text-xs"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────
  //  Single campaign view
  // ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-full">
      <div className="max-w-[1100px] mx-auto">
        <div className="mb-6 lg:mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              {status?.topic || status?.subject ? `${status.topic || status.subject} – Campaign` : "Campaign status"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {isLive ? "Live progress. You can stop, pause, or resume." : "View sent / failed and per-email details."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/campaign-status")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200"
          >
            ← All campaigns
          </button>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800 text-sm">
            {error}
          </div>
        )}

        {status && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold ${
                        status.status === "sending" && !status.paused
                          ? "bg-emerald-100 text-emerald-800"
                          : status.paused
                            ? "bg-amber-100 text-amber-800"
                            : status.status === "completed"
                              ? "bg-blue-100 text-blue-800"
                              : status.status === "cancelled"
                                ? "bg-slate-100 text-slate-700"
                                : status.status === "failed"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {status.status === "sending" && !status.paused && (
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                        </span>
                      )}
                      {status.status === "sending" && status.paused && "Paused"}
                      {status.status === "sending" && !status.paused && "Sending…"}
                      {status.status === "completed" && "Completed"}
                      {status.status === "cancelled" && "Stopped"}
                      {status.status === "failed" && "Failed"}
                    </span>
                    {status.startedAt && (
                      <span className="text-xs text-slate-500">
                        Started {formatFullDateTime(status.startedAt)}
                        {status.completedAt && ` · Ended ${formatFullDateTime(status.completedAt)}`}
                      </span>
                    )}
                  </div>
                  {isLive && (
                    <div className="flex flex-wrap items-center gap-2">
                      {status.status === "sending" && !status.paused && (
                        <button
                          type="button"
                          onClick={handlePause}
                          disabled={!!actionLoading}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 disabled:opacity-50"
                        >
                          {actionLoading === "pause" ? "Pausing…" : "Pause"}
                        </button>
                      )}
                      {status.paused && (
                        <button
                          type="button"
                          onClick={handleResume}
                          disabled={!!actionLoading}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 disabled:opacity-50"
                        >
                          {actionLoading === "resume" ? "Resuming…" : "Resume"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleStop}
                        disabled={!!actionLoading || status.cancelRequested}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {actionLoading === "stop" || status.cancelRequested ? "Stopping…" : "Stop"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <div className="flex justify-between text-sm font-medium text-slate-700 mb-2">
                    <span>Progress</span>
                    <span>
                      {status.sent + status.failed} / {status.total} · {progressPct}%
                    </span>
                  </div>
                  <div className="w-full h-3 rounded-full bg-slate-200 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <button
                    type="button"
                    onClick={() => handleTileClick("sent")}
                    aria-pressed={detailFilter === "sent"}
                    className={`rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm text-center hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400 ${
                      detailFilter === "sent" ? "ring-2 ring-emerald-400" : ""
                    }`}
                  >
                    <div className="text-3xl font-bold text-emerald-600 tabular-nums">{status.sent}</div>
                    <div className="text-sm font-semibold text-slate-700 mt-1">Sent</div>
                    <div className="text-xs text-slate-500 mt-0.5">emails delivered</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTileClick("failed")}
                    aria-pressed={detailFilter === "failed"}
                    className={`rounded-xl border border-red-200/80 bg-gradient-to-br from-red-50/80 to-white p-5 shadow-sm text-center hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-400 ${
                      detailFilter === "failed" ? "ring-2 ring-red-400" : ""
                    }`}
                  >
                    <div className="text-3xl font-bold text-red-600 tabular-nums">{status.failed}</div>
                    <div className="text-sm font-semibold text-slate-700 mt-1">Failed</div>
                    <div className="text-xs text-slate-500 mt-0.5">emails not delivered</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTileClick("all")}
                    aria-pressed={detailFilter === "all"}
                    className={`rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm text-center hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-400 ${
                      detailFilter === "all" ? "ring-2 ring-slate-400" : ""
                    }`}
                  >
                    <div className="text-3xl font-bold text-slate-800 tabular-nums">{status.total}</div>
                    <div className="text-sm font-semibold text-slate-700 mt-1">Total</div>
                    <div className="text-xs text-slate-500 mt-0.5">recipients in campaign</div>
                  </button>
                </div>

                {status.summaryBySender?.length > 0 && (
                  <div className="pt-4 border-t border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3">Summary by sender email</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {status.summaryBySender.map((summary, idx) => (
                        <div
                          key={idx}
                          className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 hover:bg-slate-50 transition-colors"
                        >
                          <div className="text-xs font-medium text-slate-600 mb-2 truncate" title={summary.fromEmail}>
                            {summary.fromEmail}
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex-1">
                              <div className="text-lg font-bold text-emerald-600 tabular-nums">{summary.sent}</div>
                              <div className="text-xs text-slate-500">Sent</div>
                            </div>
                            <div className="flex-1">
                              <div className="text-lg font-bold text-red-600 tabular-nums">{summary.failed}</div>
                              <div className="text-xs text-slate-500">Failed</div>
                            </div>
                            <div className="flex-1">
                              <div className="text-lg font-bold text-slate-700 tabular-nums">{summary.total}</div>
                              <div className="text-xs text-slate-500">Total</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(status.status === "completed" || status.status === "cancelled" || status.status === "failed") && (
                  <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate("/sales")}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700"
                    >
                      Back to Sales Team Data
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate("/sales", { state: { openSendMail: true } })}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200"
                    >
                      Send another campaign
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Detailed view */}
            <div ref={detailsRef} className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <h2 className="text-lg font-semibold text-slate-800">Detailed view</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isLive ? "Live data – each row updates as emails are sent." : "Per-email status for this campaign."}
                </p>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAllDetails((v) => !v);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                      showAllDetails
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {showAllDetails ? "Showing all" : "Show all"}
                  </button>

                  <button
                    type="button"
                    onClick={() => setDetailFilter("all")}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                      detailFilter === "all"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    All ({details.length})
                  </button>

                  <button
                    type="button"
                    onClick={() => setDetailFilter("sent")}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                      detailFilter === "sent"
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    Sent ({details.filter((d) => d?.status !== "failed").length})
                  </button>

                  <button
                    type="button"
                    onClick={() => setDetailFilter("failed")}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                      detailFilter === "failed"
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    Failed ({details.filter((d) => d?.status === "failed").length})
                  </button>

                  <span className="text-xs text-slate-500 ml-1">
                    {showAllDetails
                      ? `Showing ${filteredDetails.length} (all loaded)`
                      : `Showing ${filteredDetails.length} on this page`}
                  </span>
                </div>

                {rowNumberRange && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    Row numbers {rowNumberRange.start}–{rowNumberRange.end}
                  </p>
                )}
              </div>

              <div className="overflow-x-auto">
                {filteredDetails.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 text-sm">
                    {detailFilter === "failed"
                      ? "No failed emails on this page."
                      : detailFilter === "sent"
                        ? "No sent emails on this page yet."
                        : isLive
                          ? "No emails sent yet. Details will appear as the campaign runs."
                          : "No per-email details recorded."}
                  </div>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-28">Selected row</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">From</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">To</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-24">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider max-w-[220px]">Reason (if failed)</th>
                        {detailFilter === "failed" && (
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider w-44">
                            Action
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredDetails.map((d, idx) => {
                        const isFailed = d.status === "failed";
                        const fallbackIndex = (pagination.page - 1) * pagination.limit + idx + 1;
                        const selectedRowLabel = d.rowNumber != null ? d.rowNumber : fallbackIndex;
                        const isDeleting = deletingRows.has(selectedRowLabel.toString());

                        return (
                          <tr key={idx} className={isFailed ? "bg-red-50/50" : ""}>
                            <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">
                              {selectedRowLabel}
                            </td>
                            <td className="px-4 py-2.5 text-slate-700 truncate max-w-[200px]" title={d.fromEmail || ""}>
                              {d.fromEmail || "—"}
                            </td>
                            <td className="px-4 py-2.5 text-slate-800 truncate max-w-[240px]" title={d.email}>
                              {d.email || "—"}
                            </td>
                            <td className="px-4 py-2.5">
                              <span
                                className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                  isFailed ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"
                                }`}
                              >
                                {isFailed ? "Failed" : "Sent"}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                              {d.sentAt ? formatFullDateTime(d.sentAt) : "—"}
                            </td>
                            <td className="px-4 py-2.5 text-red-700 text-xs max-w-[220px] truncate" title={d.failureReason || ""}>
                              {isFailed ? (d.failureReason || "No reason recorded") : "—"}
                            </td>

                            {detailFilter === "failed" && (
                              <td className="px-4 py-2.5 text-center">
                                {/* <button
                                  type="button"
                                  onClick={() => handleExcludeRecipient(selectedRowLabel, d.email)}
                                  disabled={isDeleting}
                                  title="Exclude this recipient from future campaigns (does not delete the row)"
                                  className={`
                                    inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded
                                    ${isDeleting
                                      ? "bg-gray-300 text-gray-500 cursor-wait"
                                      : "bg-red-600 text-white hover:bg-red-700 active:bg-red-800"}
                                  `}
                                >
                                  {isDeleting ? "Excluding…" : "Delete from Master"}
                                </button> */}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {!showAllDetails && pagination.total > 0 && pagination.pages > 1 && (
                <div className="px-6 py-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
                  <div className="text-sm text-slate-600">
                    Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{" "}
                    <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of{" "}
                    <span className="font-medium">{pagination.total}</span> emails
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={pagination.page === 1}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ← Previous
                    </button>
                    <div className="text-sm text-slate-600 px-2">
                      Page <span className="font-medium">{pagination.page}</span> of <span className="font-medium">{pagination.pages}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                      disabled={pagination.page === pagination.pages}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {!status && !error && campaignToken && (
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-8 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-500" />
            <p className="mt-3 text-sm text-slate-500">Loading campaign status…</p>
          </div>
        )}
      </div>
    </div>
  );
}